import { formatLocation } from '@pickleballinc/lib/format-location';
import { formatName } from '@pickleballinc/lib/format-name';
import sanitizeHtml from 'sanitize-html';

import { SelectOption } from '@/modules/players/v3/types';
import { User } from '@/types/players.types';

export const getPlayerFullName = (user: User) => {
  return formatName({
    firstName: user.firstName,
    middleName: user.middleName,
    lastName: user.lastName,
    suffixName: user.nameSuffix,
    showMiddleName: user.showMiddleNameToPublic === true,
    showLastNameInitialOnly: user.showOnlyFirstInitialOfLastNameToPublic,
  });
};

export const isProPlayer = (proLevelId: number) => {
  return proLevelId !== 4 && proLevelId !== 0;
};

export const capitalizeFirstLetter = (
  value: string,
  options: {
    showOnlyFirstInitial?: boolean;
    addDotAfter?: boolean;
    addCommaAfter?: boolean;
    addSpaceAfter?: boolean;
  } = {}
) => {
  const { showOnlyFirstInitial, addDotAfter, addCommaAfter, addSpaceAfter } = options;
  return (
    (showOnlyFirstInitial ? value.charAt(0).toUpperCase() : value.charAt(0).toUpperCase()) +
    value.slice(1, showOnlyFirstInitial ? 1 : undefined) +
    (addDotAfter ? '.' : '') +
    (addCommaAfter ? ',' : '') +
    (addSpaceAfter ? ' ' : '')
  );
};

export const safeParseNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined || value === 'N/A' || value === '') {
    return 0;
  }
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isNaN(num) ? 0 : num;
};

export const getPlayerDisplayName = (player: User | undefined): string => {
  if (!player) return '';
  return [
    player.firstName,
    player.showMiddleNameToPublic && player.middleName,
    player.lastName,
    player.nameSuffix,
  ]
    .filter(Boolean)
    .join(' ');
};

export const buildPlayerLocationLine = (player: User): string => {
  const stateLabel = player.state?.abbreviation || player.state?.title;
  const countryLabel = player.country?.abbreviation2Digit || player.country?.title;
  return formatLocation([player.city, stateLabel, countryLabel]);
};

export const calculatePlayerAge = (dob: Date | undefined): number | null => {
  if (!dob) return null;
  const birth = dob instanceof Date ? dob : new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age -= 1;
  return age >= 0 ? age : null;
};

export const isSelectOption = (value: unknown): value is SelectOption =>
  value !== null && typeof value === 'object' && 'label' in value && 'value' in value;

export const safeSanitizeHtml = (htmlString: string): string => {
  if (!htmlString || typeof htmlString !== 'string') {
    return '';
  }

  const sanitizedHtml = sanitizeHtml(htmlString);

  const shortDescMatch = sanitizedHtml.match(
    /<[^>]*class="[^"]*shortdescription[^"]*"[^>]*>([\s\S]*?)<\/[^>]*>/i
  );

  if (shortDescMatch && shortDescMatch[1]) {
    return shortDescMatch[1].replace(/<[^>]*>/g, '').trim();
  }

  return sanitizedHtml.replace(/<[^>]*>/g, '').trim();
};
